import React, { useState } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { GlobalErrorBoundary } from '../components/shared/GlobalErrorBoundary';
import { ViewingLearnerBanner } from '../components/shared/ViewingLearnerBanner';
import { PortalSwitch } from './PortalSwitch';
import { PracticeView } from './PracticeView';
import { LearnerViewRouter } from './LearnerViewRouter';
import { ModalLayer } from './ModalLayer';
import { AppShell } from '../components/layout/AppShell';
import { AssessmentReportPage, AssignedAssessmentCompleteScreen } from '../assessment';
import { MODULES, type ModuleId, type Question } from '../content';
import { PORTAL_ROLES, PORTAL_LABELS } from '../config/constants';
import type { AppState } from './types';
import type { AccountsState, Account } from '../accounts';
import type { GroupId } from '../config/systemConfig';
import type { AssessmentConfig } from '../assessment';
import type { NavView } from './hooks/useAppNavigation';
import type { useAppNavigation } from './hooks/useAppNavigation';
import type { usePracticeHandlers } from '../practice';
import type { useAssessmentSession } from '../assessment';
export interface AppContentProps {
  questions: Question[];
  state: AppState;
  accounts: AccountsState;
  activeAccount: Account | null;
  selectedGroup: GroupId;
  setSelectedGroup: (g: GroupId) => void;
  startingPeriod: number;
  manualPeriod: number | null;
  setStartingPeriod: (p: number) => void;
  recordAnswer: (module: ModuleId, qId: string, correct: boolean, choice: number, payload?: string, errorType?: string, delta?: number) => void;
  markCheatSheetViewed: (moduleId: ModuleId) => void;
  clearPendingLevelUp: () => void;
  updateProfileDisplayName: (name: string) => void;
  currentLearnerDisplayName: string;
  requiresViewingLearnerSelection: boolean;
  getLearnerData: (id: string) => AppState | null;
  getLearnerModuleMastery: (id: string, moduleId: string) => number;
  viewingLearnerId: string | null;
  nav: ReturnType<typeof useAppNavigation>;
  practice: ReturnType<typeof usePracticeHandlers>;
  assessment: ReturnType<typeof useAssessmentSession>;
  handleStartAssessment: (config: AssessmentConfig) => void;
  handleAssessmentComplete: () => void;
  handleResumeAssessment: () => void;
}
export function AppContent({
  questions,
  state,
  accounts,
  activeAccount,
  selectedGroup,
  setSelectedGroup,
  startingPeriod,
  manualPeriod,
  setStartingPeriod,
  recordAnswer,
  markCheatSheetViewed,
  clearPendingLevelUp,
  updateProfileDisplayName,
  currentLearnerDisplayName,
  requiresViewingLearnerSelection,
  getLearnerData,
  getLearnerModuleMastery,
  viewingLearnerId,
  nav,
  practice,
  assessment,
  handleStartAssessment,
  handleAssessmentComplete,
  handleResumeAssessment
}: AppContentProps) {
  const [introModuleId, setIntroModuleId] = useState<ModuleId | null>(null);
  // ── Error: no questions ────────────────────────────────────────────
  if (questions.length === 0) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-id="element-760">
        <Card className="max-w-md w-full p-8 text-center" data-id="element-761">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4" data-id="element-762">
            <AlertTriangle className="w-8 h-8 text-amber-600" data-id="element-763" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-764">
            Vragen laden mislukt
          </h2>
          <Button onClick={() => window.location.reload()} variant="primary" size="lg" data-id="element-765">
            <RotateCcw className="w-4 h-4 mr-2" data-id="element-766" /> Pagina herladen
          </Button>
        </Card>
      </div>;
  }
  // ── Full-screen practice ───────────────────────────────────────────
  if (nav.currentView === 'practice') {
    return <PracticeView activeModuleId={practice.activeModuleId} activeModeId={practice.activeModeId} isFocusSession={practice.isFocusSession} focusQuestions={practice.focusQuestions} isAssessmentMode={!!assessment.assessmentAttempt} cheatSheetViewed={practice.activeModuleId ? state.progress[practice.activeModuleId]?.cheatSheetViewed ?? false : true} pendingLevelUp={state.pendingLevelUp} xp={state.stats.xp} onComplete={assessment.assessmentAttempt ? handleAssessmentComplete : practice.handlePracticeComplete} onExit={() => {
      if (assessment.assessmentAttempt) assessment.abandonCurrentAssessment();
      practice.exitPractice();
    }} onAnswer={assessment.assessmentAttempt ? (qId, correct, choice, payload, errorType, delta) => assessment.handleAnswer(qId, correct, choice, payload, errorType, delta) : (qId, correct, choice, payload, errorType, delta) => {
      const question = questions.find(q => q.id === qId);
      if (question) recordAnswer(question.module, qId, correct, choice, payload, errorType, delta);
    }} onCheatSheetView={() => practice.activeModuleId && markCheatSheetViewed(practice.activeModuleId)} onClearPendingLevelUp={clearPendingLevelUp} onReset={() => {
      practice.exitPractice();
      assessment.clearAssessmentState();
    }} data-id="element-767" />;
  }
  if (nav.currentNavView === 'assessmentReport' && assessment.assessmentReport) {
    // Assigned toetsen get a simpler completion screen — the full report
    // is still saved for the teacher portal, but students see a concise view.
    if (assessment.assessmentReport.assignmentId) {
      return <AssignedAssessmentCompleteScreen report={assessment.assessmentReport} onExit={() => {
        assessment.setAssessmentReport(null);
        nav.setCurrentNavView('home');
      }} data-id="element-768" />;
    }
    return <AssessmentReportPage report={assessment.assessmentReport} onExit={() => {
      assessment.setAssessmentReport(null);
      nav.setCurrentNavView('home');
    }} onRetake={() => {
      assessment.setAssessmentReport(null);
      nav.setCurrentNavView('assessment');
    }} onPracticeWeakAreas={() => {
      const weakModule = assessment.assessmentReport?.actionPlan?.beginMet?.moduleId;
      assessment.setAssessmentReport(null);
      if (weakModule) practice.handleStartPractice(weakModule as ModuleId);else nav.setCurrentNavView('modules');
    }} data-id="element-769" />;
  }
  // ── Portal views (Teacher/Parent/Admin) ────────────────────────────
  // Use currentNavView as the source of truth for portal visibility.
  // Previously this checked `!viewingLearnerId`, but portal shells (e.g.
  // ParentShellV2) auto-select a child via setViewingLearner when only
  // one learner is linked. That immediately set viewingLearnerId, which
  // made isPortalView false and unmounted the portal — sending the user
  // back to the learner home. Using currentNavView avoids this: the
  // portal stays visible as long as navigation intent is 'parent',
  // 'teacher', or 'admin'. Explicit "view as learner" navigation sets
  // currentNavView to 'home' via handleNavigateToLearner, which
  // correctly exits the portal.
  const portalNavViews = ['parent', 'teacher', 'admin'] as const;
  const isPortalView = activeAccount && (PORTAL_ROLES as readonly string[]).includes(activeAccount.role) && (portalNavViews as readonly string[]).includes(nav.currentNavView);
  if (isPortalView) {
    return <PortalSwitch activeAccount={activeAccount} accounts={accounts} state={state} selectedGroup={selectedGroup} questions={questions} onNavigateToLearner={nav.handleNavigateToLearner} onLogout={nav.handleLogout} onSwitchAccount={nav.handleSwitchAccount} onNavigate={view => nav.setCurrentNavView(view as NavView)} getLearnerData={getLearnerData} getLearnerModuleMastery={getLearnerModuleMastery} data-id="element-770" />;
  }
  // ── Main App with Shell ────────────────────────────────────────────
  return <>
      {viewingLearnerId && activeAccount?.role !== 'learner' && nav.currentNavView !== 'parent' && nav.currentNavView !== 'teacher' && nav.currentNavView !== 'admin' && <ViewingLearnerBanner learnerName={currentLearnerDisplayName} portalName={PORTAL_LABELS[activeAccount?.role ?? 'admin'] ?? 'Beheer'} onReturn={nav.handleReturnToPortal} data-id="element-771" />}

      <AppShell currentView={nav.currentNavView} onNavigate={view => nav.setCurrentNavView(view as NavView)} accounts={accounts} onSwitchAccount={nav.handleSwitchAccount} onLogout={nav.handleLogout} onResetDemo={nav.handleLoadDemoAccounts} data-id="element-772">
        <GlobalErrorBoundary onNavigateHome={() => nav.setCurrentNavView('home')} data-id="element-773">
          <LearnerViewRouter currentNavView={nav.currentNavView} requiresViewingLearnerSelection={requiresViewingLearnerSelection} state={state} selectedGroup={selectedGroup} modules={MODULES} questions={questions} displayName={currentLearnerDisplayName} assessmentReport={assessment.assessmentReport} accounts={accounts} startingPeriod={startingPeriod} manualPeriod={manualPeriod} onStartPractice={practice.handleStartPractice} onNavigateToModules={() => nav.setCurrentNavView('modules')} onModuleSelect={module => practice.setSelectedModule(module)} onStartAssessment={handleStartAssessment} onStartMode={practice.handleStartMode} onNavigate={view => nav.setCurrentNavView(view)} onSetStartingPeriod={setStartingPeriod} onUpdateName={updateProfileDisplayName} onSwitchAccount={nav.handleSwitchAccount} onLogout={nav.handleLogout} onCancelAssessment={() => nav.setCurrentNavView('home')} viewingLearnerId={viewingLearnerId} onExitReport={() => {
          assessment.setAssessmentReport(null);
          nav.setCurrentNavView('home');
        }} onRetakeAssessment={() => {
          assessment.setAssessmentReport(null);
          nav.setCurrentNavView('assessment');
        }} onViewReport={report => {
          assessment.setAssessmentReport(report);
          nav.setCurrentNavView('assessmentReport');
        }} onPracticeWeakAreas={() => {
          const weakModule = assessment.assessmentReport?.actionPlan?.beginMet?.moduleId;
          assessment.setAssessmentReport(null);
          if (weakModule) practice.handleStartPractice(weakModule as ModuleId);else nav.setCurrentNavView('modules');
        }} onStartPracticeForModule={(moduleId: ModuleId) => {
          assessment.setAssessmentReport(null);
          practice.handleStartPractice(moduleId);
        }} data-id="element-774" />
        </GlobalErrorBoundary>
      </AppShell>

      <ModalLayer recoverableSession={assessment.recoverableSession} onResumeAssessment={handleResumeAssessment} onAbandonRecovery={assessment.abandonRecovery} validationError={practice.validationError} selectedGroup={selectedGroup} onCloseValidationError={() => practice.setValidationError(null)} onSwitchToGroup6={() => {
      setSelectedGroup(6);
      practice.setValidationError(null);
    }} onNavigateToModules={() => {
      practice.setValidationError(null);
      nav.setCurrentNavView('modules');
    }} assessmentError={assessment.assessmentError} onCloseAssessmentError={() => assessment.setAssessmentError(null)} selectedModule={practice.selectedModule} moduleProgress={practice.selectedModule ? state.progress[practice.selectedModule.id] : null} onCloseModulePanel={() => practice.setSelectedModule(null)} onStartPractice={moduleId => practice.handleStartPractice(moduleId as ModuleId)} onViewExplanation={moduleId => {
      setIntroModuleId(moduleId as ModuleId);
      practice.setSelectedModule(null);
    }} cheatSheetModuleId={practice.cheatSheetModuleId} onCloseCheatSheet={() => practice.setCheatSheetModuleId(null)} introModuleId={introModuleId} onCloseIntroDrawer={() => setIntroModuleId(null)} data-id="element-775" />
    </>;
}